import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Search,
  RefreshCw,
  BriefcaseBusiness,
  MapPin,
  CalendarDays,
  IndianRupee,
  Users,
  Eye,
  Clock3,
  CheckCircle2,
  XCircle,
  FileText,
  Filter,
  ChevronDown,
  AlertCircle,
  X,
  UserRound,
  Building2,
  Sparkles,
  ArrowUpRight,
  Layers3,
  Target,
} from "lucide-react";

import "./Freelancecss.css";

const PROJECTS_API =
  "/api/freelance/projects";

const JOBS_API =
  "/api/jobs";

/* =========================================================
   AUTH
========================================================= */

const getAuthHeaders = () => {
  const token = localStorage.getItem("token");

  return {
    "Content-Type": "application/json",
    ...(token
      ? {
          Authorization: `Bearer ${token}`,
        }
      : {}),
  };
};

/* =========================================================
   HELPERS
========================================================= */

const cleanText = (value, fallback = "Not specified") => {
  if (
    value === null ||
    value === undefined ||
    String(value).trim() === ""
  ) {
    return fallback;
  }

  return String(value).trim();
};

const formatDate = (value) => {
  if (!value) return "Not specified";

  try {
    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
      return cleanText(value);
    }

    return date.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return cleanText(value);
  }
};

const formatCurrency = (value) => {
  if (
    value === null ||
    value === undefined ||
    value === ""
  ) {
    return "Not specified";
  }

  const numericValue = Number(
    String(value).replace(/[₹,\s]/g, "")
  );

  if (Number.isNaN(numericValue)) {
    return String(value);
  }

  return `₹${numericValue.toLocaleString("en-IN")}`;
};

const normalizeSkills = (value) => {
  if (value === null || value === undefined || value === "") return [];

  const extractSkill = (item) => {
    if (item === null || item === undefined || item === "") return [];

    if (Array.isArray(item)) {
      return item.flatMap(extractSkill);
    }

    if (typeof item === "object") {
      const directValue =
        item.name ??
        item.skill ??
        item.title ??
        item.value ??
        item.label ??
        item.skillName ??
        item.technology ??
        item.technologyName;

      if (directValue !== undefined && directValue !== null) {
        return extractSkill(directValue);
      }

      const nested =
        item.skills ??
        item.requiredSkills ??
        item.skillSet ??
        item.technologies ??
        item.items;

      return nested !== undefined ? extractSkill(nested) : [];
    }

    const text = String(item).trim();
    if (!text) return [];

    // API sometimes returns skills as a JSON string.
    if (
      (text.startsWith("[") && text.endsWith("]")) ||
      (text.startsWith("{") && text.endsWith("}"))
    ) {
      try {
        return extractSkill(JSON.parse(text));
      } catch {
        // Continue as normal text if it is not valid JSON.
      }
    }

    return text
      .split(/[,|;\n]+/)
      .map((skill) => skill.trim())
      .filter(Boolean);
  };

  return [...new Set(
    extractSkill(value)
      .map((skill) => String(skill).trim())
      .filter(Boolean)
  )];
};

/* =========================================================
   PROJECT HELPERS
========================================================= */

const getProjectId = (project) =>
  project?.id ??
  project?.projectId ??
  project?._id ??
  project?.freelanceProjectId ??
  "";

const getTitle = (project) =>
  cleanText(
    project?.title ??
      project?.projectTitle ??
      project?.name ??
      project?.projectName,
    "Untitled Freelance Project"
  );

const getDescription = (project) =>
  cleanText(
    project?.description ??
      project?.projectDescription ??
      project?.details ??
      project?.requirement,
    "No project description available."
  );

const getStatus = (project) =>
  cleanText(
    project?.status,
    "OPEN"
  ).toUpperCase();

const getSkillsArray = (project) =>
  normalizeSkills(
    project?.skills ??
      project?.requiredSkills ??
      project?.technology ??
      project?.technologies ??
      project?.skillSet
  );

const getLocation = (project) =>
  cleanText(
    project?.location ??
      project?.projectLocation ??
      project?.city ??
      project?.workLocation,
    "Remote / Not specified"
  );

const getProjectType = (project) =>
  cleanText(
    project?.projectType ??
      project?.type ??
      project?.workType,
    "Freelance"
  );

const getDeadline = (project) =>
  project?.deadline ??
  project?.dueDate ??
  project?.endDate ??
  project?.projectDeadline;

const getExperience = (project) =>
  cleanText(
    project?.experience ??
      project?.experienceRequired ??
      project?.requiredExperience,
    "Not specified"
  );

const getDuration = (project) =>
  cleanText(
    project?.duration ??
      project?.projectDuration ??
      project?.durationText,
    "Not specified"
  );

const getVacancy = (project) =>
  cleanText(
    project?.vacancy ??
      project?.vacancies ??
      project?.positions ??
      project?.numberOfPositions,
    "Not specified"
  );

const getClientName = (project) =>
  cleanText(
    project?.clientName ??
      project?.companyName ??
      project?.client ??
      project?.partnerName ??
      project?.createdByName,
    "Client"
  );

const getClientType = (project) =>
  cleanText(
    project?.clientType ??
      project?.companyType ??
      project?.userType,
    "Client"
  );

const getBidAmount = (project) =>
  project?.bidAmount ??
  project?.budget ??
  project?.amount ??
  project?.rate ??
  project?.projectBudget;

/* =========================================================
   JOB HELPERS
========================================================= */

const getJobId = (job) =>
  job?.id ??
  job?.jobId ??
  job?._id ??
  "";

const getJobTitle = (job) =>
  cleanText(
    job?.jobTitle ??
      job?.title ??
      job?.jobName ??
      job?.name,
    "Untitled Job"
  );

const getJobDescription = (job) =>
  cleanText(
    job?.jobDescription ??
      job?.description ??
      job?.jobRole ??
      job?.details,
    "No job description available."
  );

const getJobSkills = (job) =>
  normalizeSkills(
    job?.skills ??
      job?.requiredSkills ??
      job?.skillSet ??
      job?.technologies ??
      job?.technology
  );

const getJobLocation = (job) =>
  cleanText(
    job?.location ??
      job?.companyLocation ??
      job?.city ??
      job?.jobLocation,
    "Location not specified"
  );

const getJobType = (job) =>
  cleanText(
    job?.employeeType ??
      job?.employmentType ??
      job?.jobType ??
      job?.type,
    "Full Time"
  );

const getJobExperience = (job) =>
  cleanText(
    job?.experienceRequired ??
      job?.experience ??
      job?.requiredExperience,
    "Not specified"
  );

const getJobCompany = (job) =>
  cleanText(
    job?.companyName ??
      job?.companyDisplayName ??
      job?.clientName ??
      job?.partnerName,
    "Company"
  );

const getJobSalary = (job) =>
  job?.salary ??
  job?.salaryRange ??
  job?.ctc ??
  job?.budget ??
  job?.rate;

/* =========================================================
   STATUS HELPERS
========================================================= */

const getStatusClass = (status) => {
  switch (String(status).toUpperCase()) {
    case "OPEN":
      return "status-open";

    case "IN_PROGRESS":
    case "IN PROGRESS":
    case "ONGOING":
      return "status-progress";

    case "COMPLETED":
      return "status-completed";

    case "CANCELLED":
    case "CANCELED":
      return "status-cancelled";

    case "DRAFT":
      return "status-draft";

    default:
      return "status-draft";
  }
};

const getStatusIcon = (status) => {
  switch (String(status).toUpperCase()) {
    case "OPEN":
      return <CheckCircle2 size={12} />;

    case "IN_PROGRESS":
    case "IN PROGRESS":
    case "ONGOING":
      return <Clock3 size={12} />;

    case "COMPLETED":
      return <CheckCircle2 size={12} />;

    case "CANCELLED":
    case "CANCELED":
      return <XCircle size={12} />;

    default:
      return <Clock3 size={12} />;
  }
};

const getStatusLabel = (status) => {
  return String(status || "OPEN")
    .replaceAll("_", " ")
    .toLowerCase()
    .replace(/\b\w/g, (letter) =>
      letter.toUpperCase()
    );
};

/* =========================================================
   RELATED OPPORTUNITY NORMALIZER
========================================================= */

const normalizeJobOpportunity = (job) => ({
  sourceType: "JOB",
  sourceId: getJobId(job),
  rawData: job,

  title: getJobTitle(job),
  description: getJobDescription(job),
  skills: getJobSkills(job),
  location: getJobLocation(job),
  type: getJobType(job),
  experience: getJobExperience(job),
  company: getJobCompany(job),
  salary: getJobSalary(job),
  status: "APPROVED",
});

const normalizeProjectOpportunity = (project) => ({
  sourceType: "FREELANCE",
  sourceId: getProjectId(project),
  rawData: project,

  title: getTitle(project),
  description: getDescription(project),
  skills: getSkillsArray(project),
  location: getLocation(project),
  type: getProjectType(project),
  experience: getExperience(project),
  company: getClientName(project),
  salary: getBidAmount(project),
  status: getStatus(project),
});

/* =========================================================
   SKILL MATCHING
========================================================= */

const normalizeForMatch = (value) =>
  String(value || "")
    .toLowerCase()
    .replace(/[^a-z0-9+#.\s-]/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const getSkillOverlap = (skillsA, skillsB) => {
  const a = skillsA.map(normalizeForMatch);
  const b = skillsB.map(normalizeForMatch);

  if (!a.length || !b.length) return 0;

  return a.filter((skillA) =>
    b.some(
      (skillB) =>
        skillA === skillB ||
        skillA.includes(skillB) ||
        skillB.includes(skillA)
    )
  ).length;
};

const getKeywordOverlap = (a, b) => {
  const wordsA = new Set(
    normalizeForMatch(a)
      .split(" ")
      .filter((word) => word.length > 2)
  );

  const wordsB = new Set(
    normalizeForMatch(b)
      .split(" ")
      .filter((word) => word.length > 2)
  );

  let count = 0;

  wordsA.forEach((word) => {
    if (wordsB.has(word)) {
      count += 1;
    }
  });

  return count;
};

const calculateRelatedScore = (
  mainProject,
  opportunity
) => {
  const mainSkills = getSkillsArray(mainProject);

  const mainLocation = normalizeForMatch(
    getLocation(mainProject)
  );

  const mainType = normalizeForMatch(
    getProjectType(mainProject)
  );

  const mainTitle = getTitle(mainProject);
  const mainDescription = getDescription(mainProject);

  let score = 0;

  /* Skill match */
  const skillMatch = getSkillOverlap(
    mainSkills,
    opportunity.skills
  );

  score += skillMatch * 30;

  /* Location match */
  const relatedLocation = normalizeForMatch(
    opportunity.location
  );

  if (
    mainLocation &&
    relatedLocation &&
    mainLocation !== "remote / not specified" &&
    relatedLocation !== "location not specified"
  ) {
    if (
      relatedLocation.includes(mainLocation) ||
      mainLocation.includes(relatedLocation)
    ) {
      score += 25;
    }
  }

  /* Type match */
  const relatedType = normalizeForMatch(
    opportunity.type
  );

  if (
    mainType &&
    relatedType &&
    (relatedType.includes(mainType) ||
      mainType.includes(relatedType))
  ) {
    score += 10;
  }

  /* Title / description keywords */
  score +=
    getKeywordOverlap(
      mainTitle,
      opportunity.title
    ) * 5;

  score +=
    getKeywordOverlap(
      mainDescription,
      opportunity.description
    ) * 2;

  return score;
};

/* =========================================================
   COMPONENT
========================================================= */

const Freelance = () => {
  const [projects, setProjects] = useState([]);
  const [jobs, setJobs] = useState([]);

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const [error, setError] = useState("");

  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL");

  const [showFilter, setShowFilter] = useState(false);

  const [selectedProject, setSelectedProject] =
    useState(null);

  /* =======================================================
     FETCH PROJECTS + JOBS
  ======================================================= */

  const fetchProjects = useCallback(async () => {
    const response = await fetch(PROJECTS_API, {
      method: "GET",
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error(
        `Freelance API failed with status ${response.status}`
      );
    }

    const data = await response.json();

    if (Array.isArray(data)) {
      return data;
    }

    if (Array.isArray(data?.data)) {
      return data.data;
    }

    if (Array.isArray(data?.content)) {
      return data.content;
    }

    if (Array.isArray(data?.projects)) {
      return data.projects;
    }

    if (Array.isArray(data?.result)) {
      return data.result;
    }

    return [];
  }, []);

  const fetchJobs = useCallback(async () => {
    const response = await fetch(JOBS_API, {
      method: "GET",
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error(
        `Jobs API failed with status ${response.status}`
      );
    }

    const data = await response.json();

    if (Array.isArray(data)) {
      return data;
    }

    if (Array.isArray(data?.data)) {
      return data.data;
    }

    if (Array.isArray(data?.content)) {
      return data.content;
    }

    if (Array.isArray(data?.jobs)) {
      return data.jobs;
    }

    if (Array.isArray(data?.result)) {
      return data.result;
    }

    return [];
  }, []);

  const loadData = useCallback(
    async (isRefresh = false) => {
      try {
        setError("");

        if (isRefresh) {
          setRefreshing(true);
        } else {
          setLoading(true);
        }

        const results = await Promise.allSettled([
          fetchProjects(),
          fetchJobs(),
        ]);

        const projectResult = results[0];
        const jobResult = results[1];

        if (
          projectResult.status === "fulfilled"
        ) {
          setProjects(projectResult.value);
        } else {
          setProjects([]);
        }

        if (
          jobResult.status === "fulfilled"
        ) {
          setJobs(jobResult.value);
        } else {
          setJobs([]);
        }

        if (
          projectResult.status === "rejected" &&
          jobResult.status === "rejected"
        ) {
          throw new Error(
            "Unable to load freelance projects and related jobs."
          );
        }

        if (
          projectResult.status === "rejected"
        ) {
          setError(
            "Freelance projects could not be loaded. Please try again."
          );
        }
      } catch (err) {
        console.error(
          "Freelance load error:",
          err
        );

        setError(
          err?.message ||
            "Unable to load opportunities."
        );
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [fetchJobs, fetchProjects]
  );

  useEffect(() => {
    loadData();
  }, [loadData]);

  /* =======================================================
     FILTERED PROJECTS
  ======================================================= */

  const filteredProjects = useMemo(() => {
    const search = normalizeForMatch(searchTerm);

    return projects.filter((project) => {
      const status = getStatus(project);

      const searchableText = normalizeForMatch(
        [
          getTitle(project),
          getDescription(project),
          getLocation(project),
          getProjectType(project),
          getClientName(project),
          ...getSkillsArray(project),
        ].join(" ")
      );

      const matchesSearch =
        !search ||
        searchableText.includes(search);

      const matchesStatus =
        statusFilter === "ALL" ||
        status === statusFilter;

      return matchesSearch && matchesStatus;
    });
  }, [
    projects,
    searchTerm,
    statusFilter,
  ]);

  /* =======================================================
     APPROVED JOBS
  ======================================================= */

  const approvedJobs = useMemo(() => {
    return jobs.filter((job) => {
      const status = String(
        job?.status || ""
      )
        .trim()
        .toUpperCase();

      return status === "APPROVED";
    });
  }, [jobs]);

  /* =======================================================
     RELATED OPPORTUNITIES
  ======================================================= */

  const getRelatedOpportunities = useCallback(
    (project) => {
      const projectOpportunities = projects
        .filter((item) => {
          const id = getProjectId(item);

          return (
            String(id) !==
            String(getProjectId(project))
          );
        })
        .filter((item) => {
          const status = getStatus(item);

          return [
            "OPEN",
            "IN_PROGRESS",
            "ONGOING",
          ].includes(status);
        })
        .map(normalizeProjectOpportunity);

      const jobOpportunities =
        approvedJobs.map(
          normalizeJobOpportunity
        );

      const combined = [
        ...projectOpportunities,
        ...jobOpportunities,
      ];

      return combined
        .map((opportunity) => ({
          ...opportunity,
          score: calculateRelatedScore(
            project,
            opportunity
          ),
        }))
        .sort((a, b) => {
          if (b.score !== a.score) {
            return b.score - a.score;
          }

          return a.title.localeCompare(
            b.title
          );
        })
        .slice(0, 8);
    },
    [projects, approvedJobs]
  );

  /* =======================================================
     DEFAULT RELATED CONTENT
  ======================================================= */

  const defaultRelatedOpportunities = useMemo(() => {
    const projectOpportunities = projects
      .filter((project) =>
        [
          "OPEN",
          "IN_PROGRESS",
          "ONGOING",
        ].includes(getStatus(project))
      )
      .map(normalizeProjectOpportunity);

    const jobOpportunities =
      approvedJobs.map(
        normalizeJobOpportunity
      );

    return [
      ...projectOpportunities,
      ...jobOpportunities,
    ]
      .sort((a, b) => {
        if (a.sourceType === "JOB" && b.sourceType !== "JOB") {
          return -1;
        }

        if (
          a.sourceType !== "JOB" &&
          b.sourceType === "JOB"
        ) {
          return 1;
        }

        return a.title.localeCompare(
          b.title
        );
      })
      .slice(0, 8);
  }, [projects, approvedJobs]);

  /* =======================================================
     OPEN PROJECT MODAL
  ======================================================= */

  const openProjectModal = (project) => {
    setSelectedProject(project);
  };

  const openRelatedOpportunity = (
    opportunity
  ) => {
    if (
      opportunity.sourceType === "FREELANCE"
    ) {
      setSelectedProject(
        opportunity.rawData
      );
      return;
    }

    const job = opportunity.rawData;

    setSelectedProject({
      ...job,

      __isJob: true,

      title: getJobTitle(job),
      projectTitle: getJobTitle(job),

      description:
        getJobDescription(job),

      skills: getJobSkills(job),

      location:
        getJobLocation(job),

      projectType:
        getJobType(job),

      experience:
        getJobExperience(job),

      clientName:
        getJobCompany(job),

      companyName:
        getJobCompany(job),

      bidAmount:
        getJobSalary(job),

      status: "APPROVED",
    });
  };

  /* =======================================================
     ESC CLOSE MODAL
  ======================================================= */

  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === "Escape") {
        setSelectedProject(null);
        setShowFilter(false);
      }
    };

    window.addEventListener(
      "keydown",
      handleEscape
    );

    return () => {
      window.removeEventListener(
        "keydown",
        handleEscape
      );
    };
  }, []);

  /* =======================================================
     RESET
  ======================================================= */

  const resetFilters = () => {
    setSearchTerm("");
    setStatusFilter("ALL");
  };

  const hasFilters =
    Boolean(searchTerm) ||
    statusFilter !== "ALL";

  /* =======================================================
     MODAL DATA
  ======================================================= */

  const isSelectedJob =
    selectedProject?.__isJob === true;

  const modalTitle = selectedProject
    ? getTitle(selectedProject)
    : "";

  const modalDescription = selectedProject
    ? getDescription(selectedProject)
    : "";

  const modalSkills = selectedProject
    ? getSkillsArray(selectedProject)
    : [];

  const modalLocation = selectedProject
    ? getLocation(selectedProject)
    : "";

  const modalType = selectedProject
    ? getProjectType(selectedProject)
    : "";

  const modalExperience =
    selectedProject
      ? getExperience(selectedProject)
      : "";

  const modalDuration =
    selectedProject
      ? getDuration(selectedProject)
      : "";

  const modalDeadline =
    selectedProject
      ? getDeadline(selectedProject)
      : null;

  const modalVacancy =
    selectedProject
      ? getVacancy(selectedProject)
      : "";

  const modalClient =
    selectedProject
      ? getClientName(selectedProject)
      : "";

  const modalClientType =
    selectedProject
      ? getClientType(selectedProject)
      : "";

  const modalBid =
    selectedProject
      ? getBidAmount(selectedProject)
      : null;

  /* =======================================================
     RENDER
  ======================================================= */

  return (
    <div className="freelance-page">

      {/* ===================================================
          HEADER
      =================================================== */}

      <header className="freelance-header">

        <div className="header-left">

          <div className="header-icon">
            <BriefcaseBusiness
              size={22}
            />
          </div>

          <div className="header-copy">

            <p className="premium-eyebrow">
              Trainer Marketplace
            </p>

            <h1>
              Freelance Opportunities
            </h1>

            <p>
              Discover freelance projects and
              relevant training opportunities
              matched to your expertise.
            </p>

          </div>

        </div>

        <div className="header-actions">

          <button
            type="button"
            className="refresh-btn"
            onClick={() => loadData(true)}
            disabled={refreshing}
          >
            <RefreshCw
              size={15}
              className={
                refreshing ? "spin" : ""
              }
            />

            <span>
              {refreshing
                ? "Refreshing..."
                : "Refresh"}
            </span>
          </button>

        </div>

      </header>

      {/* ===================================================
          SEARCH TOOLBAR
      =================================================== */}

      <div className="project-toolbar">

        <div className="search-container">

          <Search
            size={17}
            className="search-icon"
          />

          <input
            type="text"
            placeholder="Search projects, skills, location, client..."
            value={searchTerm}
            onChange={(event) =>
              setSearchTerm(
                event.target.value
              )
            }
          />

          {searchTerm && (
            <button
              type="button"
              className="clear-btn"
              onClick={() =>
                setSearchTerm("")
              }
              aria-label="Clear search"
            >
              <X size={14} />
            </button>
          )}

        </div>

        <div className="filter-container">

          <button
            type="button"
            className="filter-btn"
            onClick={() =>
              setShowFilter(
                (previous) => !previous
              )
            }
          >
            <span className="filter-btn-left">
              <Filter size={14} />
              {statusFilter === "ALL"
                ? "All Status"
                : getStatusLabel(
                    statusFilter
                  )}
            </span>

            <ChevronDown
              size={14}
              className={`filter-chevron ${
                showFilter
                  ? "active"
                  : ""
              }`}
            />
          </button>

          {showFilter && (
            <div className="filter-dropdown">

              {[
                {
                  label: "All Status",
                  value: "ALL",
                },
                {
                  label: "Open",
                  value: "OPEN",
                },
                {
                  label: "In Progress",
                  value: "IN_PROGRESS",
                },
                {
                  label: "Completed",
                  value: "COMPLETED",
                },
                {
                  label: "Cancelled",
                  value: "CANCELLED",
                },
              ].map((option) => (
                <button
                  type="button"
                  key={option.value}
                  className={`filter-option ${
                    statusFilter ===
                    option.value
                      ? "active"
                      : ""
                  }`}
                  onClick={() => {
                    setStatusFilter(
                      option.value
                    );
                    setShowFilter(false);
                  }}
                >
                  {option.label}
                </button>
              ))}

            </div>
          )}

        </div>

      </div>

      {/* ===================================================
          RESULT BAR
      =================================================== */}

      <div className="result-bar">

        <p className="result-count">
          Showing{" "}
          <strong>
            {filteredProjects.length}
          </strong>{" "}
          freelance{" "}
          {filteredProjects.length === 1
            ? "project"
            : "projects"}
        </p>

        {hasFilters && (
          <button
            type="button"
            className="clear-filter-btn"
            onClick={resetFilters}
          >
            Clear filters
          </button>
        )}

      </div>

      {/* ===================================================
          ERROR
      =================================================== */}

      {error && (
        <div className="error-box">

          <AlertCircle
            size={18}
            className="error-icon"
          />

          <div className="error-content">

            <strong>
              Unable to load all opportunities
            </strong>

            <p>{error}</p>

            <button
              type="button"
              onClick={() =>
                loadData(true)
              }
            >
              Try Again
            </button>

          </div>

        </div>
      )}

      {/* ===================================================
          LOADING
      =================================================== */}

      {loading ? (
        <div className="loading-state">

          <div className="loading-orb" />

          <p>
            Loading opportunities...
          </p>

        </div>
      ) : (
        <div className="freelance-content-layout">

          {/* =============================================
              LEFT - 65% FREELANCE PROJECTS
          ============================================= */}

          <main className="freelance-main-column">

            <div className="section-heading">

              <div>
                <span>
                  FREELANCE PROJECTS
                </span>

                <h2>
                  Available Projects
                </h2>
              </div>

              <div className="section-count">
                {filteredProjects.length}
              </div>

            </div>

            {filteredProjects.length ===
            0 ? (
              <div className="empty-state">

                <div className="empty-icon">
                  <BriefcaseBusiness
                    size={24}
                  />
                </div>

                <h3>
                  No freelance projects found
                </h3>

                <p>
                  We couldn't find any projects
                  matching your current search
                  or filter criteria.
                </p>

                {hasFilters && (
                  <button
                    type="button"
                    className="reset-btn"
                    onClick={resetFilters}
                  >
                    Reset Filters
                  </button>
                )}

              </div>
            ) : (
              <div className="project-grid">

                {filteredProjects.map(
                  (project, index) => {

                    const projectId =
                      getProjectId(
                        project
                      );

                    const status =
                      getStatus(project);

                    const skills =
                      getSkillsArray(
                        project
                      );

                    return (
                      <article
                        className="project-card"
                        key={
                          projectId ||
                          `project-${index}`
                        }
                      >

                        {/* CARD HEADER */}

                        <div className="card-header">

                          <div className="project-title-area">

                            <div className="project-icon">
                              <BriefcaseBusiness
                                size={19}
                              />
                            </div>

                            <div className="project-title-copy">

                              <span className="card-project-label">
                                Freelance Project
                              </span>

                              <h2>
                                {getTitle(
                                  project
                                )}
                              </h2>

                              <div className="title-meta">

                                <span>
                                  {getProjectType(
                                    project
                                  )}
                                </span>

                                <span className="meta-dot" />

                                <span>
                                  {getClientName(
                                    project
                                  )}
                                </span>

                              </div>

                            </div>

                          </div>

                          <div className="status-row">

                            <span
                              className={`project-status ${getStatusClass(
                                status
                              )}`}
                            >
                              {getStatusIcon(
                                status
                              )}

                              {getStatusLabel(
                                status
                              )}
                            </span>

                          </div>

                        </div>

                        {/* DESCRIPTION */}

                        <div className="project-description-wrap">

                          <p className="project-description">
                            {getDescription(
                              project
                            )}
                          </p>

                        </div>

                        {/* SKILLS */}

                        <div className="technology-box">

                          <div className="technology-heading">

                            <Sparkles
                              size={13}
                            />

                            <span>
                              Required Skills
                            </span>

                            {skills.length >
                              0 && (
                              <span className="skill-count">
                                {skills.length} {skills.length === 1 ? "skill" : "skills"}
                              </span>
                            )}

                          </div>

                          {skills.length >
                          0 ? (
                            <div className="skills-list">

                              {skills
                                .slice(0, 7)
                                .map(
                                  (
                                    skill,
                                    skillIndex
                                  ) => (
                                    <span
                                      className="skill-badge"
                                      key={`${skill}-${skillIndex}`}
                                      title={skill}
                                    >
                                      {skill}
                                    </span>
                                  )
                                )}

                              {skills.length >
                                7 && (
                                <span className="skill-badge more">
                                  +
                                  {skills.length -
                                    7}{" "}
                                  more
                                </span>
                              )}

                            </div>
                          ) : (
                            <p className="no-skills">
                              No specific skills
                              listed.
                            </p>
                          )}

                        </div>

                        {/* DETAILS */}

                        <div className="project-details">

                          <div className="project-detail">

                            <MapPin
                              size={15}
                              className="detail-icon"
                            />

                            <label>
                              Location
                            </label>

                            <strong
                              title={getLocation(
                                project
                              )}
                            >
                              {getLocation(
                                project
                              )}
                            </strong>

                          </div>

                          <div className="project-detail">

                            <CalendarDays
                              size={15}
                              className="detail-icon"
                            />

                            <label>
                              Deadline
                            </label>

                            <strong>
                              {formatDate(
                                getDeadline(
                                  project
                                )
                              )}
                            </strong>

                          </div>

                          <div className="project-detail">

                            <Clock3
                              size={15}
                              className="detail-icon"
                            />

                            <label>
                              Duration
                            </label>

                            <strong
                              title={getDuration(
                                project
                              )}
                            >
                              {getDuration(
                                project
                              )}
                            </strong>

                          </div>

                          <div className="project-detail">

                            <Users
                              size={15}
                              className="detail-icon"
                            />

                            <label>
                              Positions
                            </label>

                            <strong>
                              {getVacancy(
                                project
                              )}
                            </strong>

                          </div>

                        </div>

                        {/* FOOTER */}

                        <div className="card-footer">

                          <div className="client-mini">

                            <div className="client-mini-icon">

                              <Building2
                                size={15}
                              />

                            </div>

                            <div>

                              <strong
                                title={getClientName(
                                  project
                                )}
                              >
                                {getClientName(
                                  project
                                )}
                              </strong>

                              <span>
                                {getClientType(
                                  project
                                )}
                              </span>

                            </div>

                          </div>

                          <div className="card-actions">

                            <div className="bid-amount-box">

                              <span>
                                Budget / Rate
                              </span>

                              <strong>
                                {formatCurrency(
                                  getBidAmount(
                                    project
                                  )
                                )}
                              </strong>

                            </div>

                            <button
                              type="button"
                              className="view-btn"
                              onClick={() =>
                                openProjectModal(
                                  project
                                )
                              }
                            >
                              <Eye
                                size={14}
                              />

                              View Details

                              <ArrowUpRight
                                size={12}
                              />
                            </button>

                          </div>

                        </div>

                      </article>
                    );
                  }
                )}

              </div>
            )}

          </main>

          {/* =============================================
              RIGHT - 35% RELATED OPPORTUNITIES
          ============================================= */}

          <aside className="related-sidebar">

            <div className="related-panel">

              <div className="related-header">

                <div className="related-heading">

                  <div className="related-heading-icon">
                    <Target
                      size={17}
                    />
                  </div>

                  <div>
                    <span>
                      RECOMMENDED
                    </span>

                    <h2>
                      Related Opportunities
                    </h2>
                  </div>

                </div>

                <span className="related-total">
                  {selectedProject
                    ? getRelatedOpportunities(
                        selectedProject
                      ).length
                    : defaultRelatedOpportunities.length}
                </span>

              </div>

              <p className="related-subtitle">
                Jobs and projects related to
                skills, role and location.
              </p>

              <div className="related-list">

                {(selectedProject
                  ? getRelatedOpportunities(
                      selectedProject
                    )
                  : defaultRelatedOpportunities
                ).map(
                  (
                    opportunity,
                    opportunityIndex
                  ) => (

                    <button
                      type="button"
                      className="related-card"
                      key={`${opportunity.sourceType}-${opportunity.sourceId || opportunityIndex}`}
                      onClick={() =>
                        openRelatedOpportunity(
                          opportunity
                        )
                      }
                    >

                      <div className="related-card-top">

                        <span
                          className={`opportunity-type ${
                            opportunity.sourceType ===
                            "JOB"
                              ? "type-job"
                              : "type-freelance"
                          }`}
                        >
                          {opportunity.sourceType ===
                          "JOB"
                            ? "JOB"
                            : "FREELANCE"}
                        </span>

                        <ArrowUpRight
                          size={14}
                          className="related-arrow"
                        />

                      </div>

                      <h3>
                        {opportunity.title}
                      </h3>

                      <div className="related-company">

                        <Building2
                          size={12}
                        />

                        <span>
                          {opportunity.company}
                        </span>

                      </div>

                      <div className="related-location">

                        <MapPin
                          size={12}
                        />

                        <span>
                          {opportunity.location}
                        </span>

                      </div>

                      {opportunity.skills
                        ?.length > 0 && (
                        <div className="related-skills">

                          {opportunity.skills
                            .slice(0, 3)
                            .map(
                              (
                                skill,
                                skillIndex
                              ) => (
                                <span
                                  key={`${skill}-${skillIndex}`}
                                  title={skill}
                                >
                                  {skill}
                                </span>
                              )
                            )}

                          {opportunity.skills
                            .length > 3 && (
                            <span>
                              +
                              {opportunity
                                .skills
                                .length -
                                3}
                            </span>
                          )}

                        </div>
                      )}

                      <div className="related-footer">

                        <span>
                          {opportunity.sourceType ===
                          "JOB"
                            ? "Salary / CTC"
                            : "Budget"}
                        </span>

                        <strong>
                          {formatCurrency(
                            opportunity.salary
                          )}
                        </strong>

                      </div>

                    </button>

                  )
                )}

              </div>

              {(selectedProject
                ? getRelatedOpportunities(
                    selectedProject
                  ).length
                : defaultRelatedOpportunities.length) ===
                0 && (
                <div className="related-empty">

                  <Layers3
                    size={22}
                  />

                  <strong>
                    No related opportunities
                  </strong>

                  <p>
                    More matching jobs and
                    projects will appear here as
                    they become available.
                  </p>

                </div>
              )}

            </div>

          </aside>

        </div>
      )}

      {/* ===================================================
          PROJECT / JOB MODAL
      =================================================== */}

      {selectedProject && (
        <div
          className="modal-overlay"
          onMouseDown={(event) => {
            if (
              event.target ===
              event.currentTarget
            ) {
              setSelectedProject(null);
            }
          }}
        >

          <div
            className="project-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="project-modal-title"
          >

            <div className="modal-header">

              <div className="modal-icon">

                {isSelectedJob ? (
                  <FileText
                    size={20}
                  />
                ) : (
                  <BriefcaseBusiness
                    size={20}
                  />
                )}

              </div>

              <div className="modal-title">

                <span className="modal-eyebrow">
                  {isSelectedJob
                    ? "Approved Job"
                    : "Freelance Project"}
                </span>

                <h2 id="project-modal-title">
                  {modalTitle}
                </h2>

              </div>

              <button
                type="button"
                className="modal-close"
                onClick={() =>
                  setSelectedProject(null)
                }
                aria-label="Close"
              >
                <X size={17} />
              </button>

            </div>

            <div className="modal-body">

              <div className="modal-status-row">

                <span
                  className={`project-status ${getStatusClass(
                    getStatus(
                      selectedProject
                    )
                  )}`}
                >
                  {getStatusIcon(
                    getStatus(
                      selectedProject
                    )
                  )}

                  {isSelectedJob
                    ? "Approved"
                    : getStatusLabel(
                        getStatus(
                          selectedProject
                        )
                      )}
                </span>

                <span className="project-type-badge">
                  {modalType}
                </span>

              </div>

              {/* DESCRIPTION */}

              <section className="modal-section">

                <h3>
                  About the Opportunity
                </h3>

                <p>
                  {modalDescription}
                </p>

              </section>

              {/* SKILLS */}

              <section className="modal-section">

                <h3>
                  Required Skills
                </h3>

                {modalSkills.length >
                0 ? (
                  <div className="modal-skills">

                    {modalSkills.map(
                      (
                        skill,
                        index
                      ) => (
                        <span
                          className="modal-skill-badge"
                          key={`${skill}-${index}`}
                          title={skill}
                        >
                          {skill}
                        </span>
                      )
                    )}

                  </div>
                ) : (
                  <p>
                    No specific skills listed.
                  </p>
                )}

              </section>

              {/* INFORMATION */}

              <section className="modal-section">

                <h3>
                  Opportunity Details
                </h3>

                <div className="modal-info-grid">

                  <div className="modal-info-item">

                    <label>
                      Location
                    </label>

                    <strong>
                      {modalLocation}
                    </strong>

                  </div>

                  <div className="modal-info-item">

                    <label>
                      Experience
                    </label>

                    <strong>
                      {modalExperience}
                    </strong>

                  </div>

                  {!isSelectedJob && (
                    <>
                      <div className="modal-info-item">

                        <label>
                          Duration
                        </label>

                        <strong>
                          {modalDuration}
                        </strong>

                      </div>

                      <div className="modal-info-item">

                        <label>
                          Deadline
                        </label>

                        <strong>
                          {formatDate(
                            modalDeadline
                          )}
                        </strong>

                      </div>

                      <div className="modal-info-item">

                        <label>
                          Positions
                        </label>

                        <strong>
                          {modalVacancy}
                        </strong>

                      </div>
                    </>
                  )}

                  <div className="modal-info-item bid-info-item">

                    <label>
                      {isSelectedJob
                        ? "Salary / CTC"
                        : "Budget / Rate"}
                    </label>

                    <strong>
                      {formatCurrency(
                        modalBid
                      )}
                    </strong>

                  </div>

                </div>

              </section>

              {/* CLIENT */}

              <section className="modal-section">

                <h3>
                  {isSelectedJob
                    ? "Company"
                    : "Client"}
                </h3>

                <div className="client-box">

                  <div className="client-icon">

                    {isSelectedJob ? (
                      <Building2
                        size={18}
                      />
                    ) : (
                      <UserRound
                        size={18}
                      />
                    )}

                  </div>

                  <div>

                    <span className="client-name">
                      {modalClient}
                    </span>

                    <span className="client-type">
                      {modalClientType}
                    </span>

                  </div>

                </div>

              </section>

            </div>

            <div className="modal-footer">

              <button
                type="button"
                className="modal-primary-btn"
                onClick={() =>
                  setSelectedProject(null)
                }
              >
                Close Details
              </button>

            </div>

          </div>

        </div>
      )}

    </div>
  );
};

export default Freelance;